import React, { useMemo } from 'react';

import parse, { domToReact } from 'html-react-parser';
import { get } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import { PublicTitle, Question, Attachments } from '../components';
import AudioPlayer from '../../audio-player';
import ChoiceChip from '../../choice-chip';
import ImageGallery from '../../image-gallery';
import FillChoicheSelect from './components/fill-choiche-select';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import { exerciseDefaultProps, exercisePropTypes } from '../../../constants/exercise';
import { parseElement } from '../../../lib/exercise';
import Geogebra from '../../geogebra';
import Stack from '../../stack';
import MathContent from '../../math-content';
import { parseMfenced } from '../../../lib/utils';

export const handleClickFillChoice = (exerciseId, answers, gapId, optionId, onAnswer) => {
    let newAnswers = answers.filter((answer) => !(answer.gapId === gapId && answer.optionId === optionId));

    if (newAnswers.length !== answers.length) {
        onAnswer(exerciseId, newAnswers);
        return;
    }

    newAnswers = answers.filter((answer) => !(answer.gapId === gapId));

    newAnswers.push({ gapId, optionId });

    onAnswer(exerciseId, newAnswers);
};

export const getType = (answered, groupAnswered, correctionStatus, isCorrect) => {
    if (correctionStatus) {
        if ((!answered && !isCorrect) || (groupAnswered && !answered)) {
            return 'listed';
        }

        return 'validated';
    }

    if (answered) {
        return 'selected';
    }

    return 'listed';
};

export const getValidationType = (answered, isCorrect) => {
    if (!answered) {
        return 'missing';
    }

    return isCorrect ? 'success' : 'error';
};

export const getValue = (optionsValues, groupAswered) => {
    if (groupAswered) {
        return optionsValues.find((option) => option.id === +groupAswered?.optionId).text;
    }

    return optionsValues.find((item) => item.correct === true).text;
};

const FillChoice = ({
    exercise,
    answers = exerciseDefaultProps.answers,
    onAnswer,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    solution,
    hasAttachments = exerciseDefaultProps.hasAttachments,
}) => {
    let question = '';
    if (exercise.question) {
        question = exercise.question.replace(/<gap_ref (.*?)\/>/g, '<gap_ref $1></gap_ref>');
    }

    const showGrid = (hasAttachments.images || hasAttachments.video) && show.attachments;

    const parsedGaps = useMemo(() => parseElement(exercise.question, exercise.options), [exercise]);
    let previousWord = '';
    let nextWord = '';
    let indexPrevWord = 0;
    let indexNextWord = 0;

    const options = {
        replace: (domNode) => {
            if (domNode.name && domNode.name === 'mfenced') {
                return parseMfenced(domNode, options);
            }

            if (exercise.useSelect) {
                if (domNode.type === 'text' && domNode?.next?.name === 'gap_ref') {
                    indexPrevWord = domNode.data.length - domNode.data.lastIndexOf(' ');
                    if (domNode.data.lastIndexOf(' ') !== -1) {
                        previousWord = domNode.data.substr(domNode.data.length - indexPrevWord);
                    } else {
                        previousWord = domNode.data.substr(0, domNode.data.length);
                    }

                    if (previousWord !== ' ') {
                        domNode.data = domNode.data.slice(0, -indexPrevWord + 1);
                    }
                    return domNode;
                }
            }

            if (domNode.name && domNode.name === 'gap_ref') {
                if (exercise.useSelect) {
                    if (domNode?.next?.data) {
                        indexNextWord = domNode.next.data.indexOf(' ');
                        nextWord = domNode?.next?.data.substr(0, indexNextWord);
                        if (nextWord !== ' ') {
                            domNode.next.data = domNode?.next?.data.substr(indexNextWord, domNode.next.data.length);
                        }
                    }
                }

                const gapId = domNode.attribs.idref.replace('_', '');

                const [options] = exercise.options.filter((list) => list.id === gapId);
                const groupAnswered = answers.find((answer) => answer.gapId === gapId);
                const parsedGap = parsedGaps.get(domNode.attribs.idref);
                if (exercise.useSelect && !solution) {
                    const prevGapNext = (
                        <span style={{ display: 'inline-block' }}>
                            {previousWord}
                            <FillChoicheSelect
                                items={options?.values}
                                onSelectionChange={(itemId) => handleClickFillChoice(exercise.id, answers, gapId, `${itemId}`, onAnswer)}
                                selectedKey={+groupAnswered?.optionId || null}
                                isValidated={!!correctionStatus}
                                badgeIndex={parsedGap.char}
                                value={getValue(options?.values, groupAnswered)}
                                validationType={getValidationType(
                                    !!options.values.find((option) => option.id === +groupAnswered?.optionId),
                                    options.values.find((item) => item.id === +groupAnswered?.optionId)?.correct,
                                )}
                            />
                            {nextWord}
                        </span>
                    );
                    previousWord = '';
                    nextWord = '';
                    return prevGapNext;
                }
                return (
                    <>
                        {options.values.map((option) => (
                            <ChoiceChip
                                id={`${option.id}`}
                                key={`${option.id}`}
                                badgeIndex={parsedGap.char}
                                handleClick={() => handleClickFillChoice(exercise.id, answers, gapId, `${option.id}`, onAnswer)}
                                value={option.text}
                                type={getType(
                                    answers.find((answer) => answer.gapId === gapId && answer.optionId === `${option.id}`),
                                    groupAnswered,
                                    correctionStatus,
                                    option.correct,
                                )}
                                disabled={!!correctionStatus}
                                validationType={getValidationType(
                                    answers.find((answer) => answer.gapId === gapId && answer.optionId === `${option.id}`),
                                    option.correct,
                                )}
                            />
                        ))}
                    </>
                );
            }
        },
    };

    let parsedQuestion = parse(question, options);

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Show when={show.prompt && exercise.prompt}>
                <FluidContainer paddingBottom={24}>
                    <MathContent>{exercise.prompt}</MathContent>
                </FluidContainer>
            </Show>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>

            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>
                <Question>{parsedQuestion}</Question>
            </GridContainer>
        </>
    );
};

FillChoice.propTypes = exercisePropTypes;

export default FillChoice;
